import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, computed, input, output, signal } from '@angular/core';
import { ClipboardService, DataTestDirective, TargetUrl, User, WebShareService, captureSentryMessage } from '@outlaw/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { CommunityItem, OwnerActionEventsEnum, Selection } from '../../../../interfaces';
import { VibrateService } from '../../../../services/util/vibrate.service';
import { PostService } from '../../../../services/api/post.service';
import { SelectionService } from '../../../../services/api/selection.service';
import { SelectionDialogService } from '../../../../services/api/selection-dialog.service';
import {
  CommunityNavigationService,
  FeatureFlagService,
  GuestAuthService,
  CommunityMobileEventsService,
  SnackbarService,
  SelectionStateService,
} from '../../../../services';
import { ExternalPostNavigationService } from '../../../../services/navigation/external-post-navigation.service';
import { COMMUNITY_NAVIGATION_CONFIG } from '../../../../providers/community-navigation-config.provider';
import { NgTemplateOutlet } from '@angular/common';
import { OwnerActionsComponent } from '../owner-actions/owner-actions.component';
import { SelectionsMenuComponent } from '../../../selections-menu/selections-menu.component';
import { IconComponent } from '@outlaw/icons';

@Component({
  selector: 'app-post-actions',
  templateUrl: './post-actions.component.html',
  styleUrls: ['./post-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DataTestDirective, NgTemplateOutlet, OwnerActionsComponent, IconComponent, SelectionsMenuComponent],
})
export class PostActionsComponent {
  item = input.required<CommunityItem>();
  currentUser = input<User>();
  showFooter = input<boolean>();
  isOwner = input<boolean>();
  isGuest = input<boolean>();
  isMajiApp = input<boolean>(false);
  variant = input<'primary' | 'secondary'>('primary');
  itemChange = output<CommunityItem>();
  ownerAction = output<OwnerActionEventsEnum>();
  shareLinkCopied = output<void>();
  openGuestView = output<void>();
  navigateToPost = output<TargetUrl>();
  saveToggled = output<CommunityItem>();

  isSaved = signal<boolean | undefined>(undefined);

  selectionsMenuOpen = signal(false);
  selections = signal<Selection[]>([]);
  selectionsLoading = signal(false);

  #pendingChanges = new Map<number, { selectionId: number; add: boolean; posts_uuids: string[] }>();
  #dialogOpen = signal(false);
  #closeTimer: ReturnType<typeof setTimeout> | null = null;

  isSavedDisplay = computed(() => this.isSaved() ?? this.item().is_saved ?? false);

  private postService = inject(PostService);
  private selectionService = inject(SelectionService);
  private selectionStateService = inject(SelectionStateService);
  private selectionDialogService = inject(SelectionDialogService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private vibrateService = inject(VibrateService);
  private clipboardService = inject(ClipboardService);
  private featureFlagService = inject(FeatureFlagService);
  private navigationService = inject(CommunityNavigationService);
  private snackbarService = inject(SnackbarService);
  private mobileEventsService = inject(CommunityMobileEventsService);
  private guestAuthService = inject(GuestAuthService);
  private webShareService = inject(WebShareService);
  private externalPostNavService = inject(ExternalPostNavigationService);
  #navConfig = inject(COMMUNITY_NAVIGATION_CONFIG, { optional: true });

  postUrl = computed(() => {
    if (this.variant() !== 'primary') return null;

    if (this.#navConfig?.useExternalPostNavigation) {
      return this.externalPostNavService.getExternalPostUrl(this.item()) || null;
    }

    return this.navigationService.getPostUrl(this.item().post_uuid);
  });

  get commentsAmount(): number {
    return this.item().comments_count || 0;
  }

  get canDisplayComments(): boolean {
    return this.featureFlagService.isCommentsEnabled();
  }

  get canDisplayOwnerActions(): boolean {
    return this.isOwner() && !this.featureFlagService.isCommunityInApp();
  }

  get isMajiWebview(): boolean {
    return this.mobileEventsService.isWebView && this.isMajiApp();
  }

  get canDisplaySelections(): boolean {
    return this.featureFlagService.isSelectionsEnabled();
  }

  savePost(e: Event): void {
    e.preventDefault();
    e.stopPropagation();

    if (this.isGuest()) {
      this.guestAuthService.handleGuestAuth({
        postId: this.item().post_uuid,
        onCommunityInApp: () => this.openGuestView.emit(),
      });
      return;
    }

    this.vibrateService.vibrate();

    const allSelections = this.selectionStateService.selections();
    const postUuid = this.item().post_uuid;

    if (this.isSavedDisplay()) {
      // Unsave: remove from all selections that contain this post
      this.selections.set(allSelections);
      const containing = allSelections.filter(s => s.posts_uuids.includes(postUuid));
      containing.forEach(s => this.toggleSelection(s));
      this.flushPendingChanges();
      this.saveToggled.emit({ ...this.item(), is_saved: false });
    } else if (allSelections.length > 0) {
      // Save: add to first selection
      this.selections.set(allSelections);
      this.toggleSelection(allSelections[0]);
      this.flushPendingChanges();
      this.saveToggled.emit({ ...this.item(), is_saved: true });
    } else {
      this.openSelectionsMenu();
    }
  }

  onSaveHover(): void {
    this.cancelCloseTimer();
    if (this.isGuest() || this.selectionsMenuOpen()) return;
    this.openSelectionsMenu();
  }

  onSaveLeave(): void {
    this.scheduleClose();
  }

  onMenuMouseEnter(): void {
    this.cancelCloseTimer();
  }

  onMenuMouseLeave(): void {
    this.scheduleClose();
  }

  private scheduleClose(): void {
    this.cancelCloseTimer();
    if (!this.selectionsMenuOpen() || this.#dialogOpen()) return;
    this.#closeTimer = setTimeout(() => this.closeSelectionsMenu(), 200);
  }

  private cancelCloseTimer(): void {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  toggleSelection(selection: Selection): void {
    const postUuid = this.item().post_uuid;
    const isInSelection = selection.posts_uuids.includes(postUuid);

    // Optimistic UI — instant, no API call
    if (isInSelection) {
      this.selectionStateService.removePostFromSelection(selection.id, postUuid);
      this.selections.update(list =>
        list.map(s => s.id === selection.id
          ? { ...s, posts_uuids: s.posts_uuids.filter(u => u !== postUuid) }
          : s)
      );
    } else {
      this.selectionStateService.addPostToSelection(selection.id, postUuid);
      this.selections.update(list =>
        list.map(s => s.id === selection.id
          ? { ...s, posts_uuids: [...s.posts_uuids, postUuid] }
          : s)
      );
    }

    const hasSaved = this.selections().some(s => s.posts_uuids.includes(postUuid));
    this.isSaved.set(hasSaved);

    // Queue change — will be flushed on menu close
    const updated = this.selections().find(s => s.id === selection.id);
    if (updated) {
      this.#pendingChanges.set(selection.id, {
        selectionId: selection.id,
        add: !isInSelection,
        posts_uuids: [...updated.posts_uuids],
      });
    }

    this.cdr.markForCheck();
  }

  createNewSelection(): void {

    const user = this.currentUser();
    if (!user) return;

    this.#dialogOpen.set(true);

    this.selectionDialogService.openAddDialog().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      setTimeout(() => this.#dialogOpen.set(false));

      if (!result?.name) return;

      const postUuid = this.item().post_uuid;
      this.selectionService
        .createSelection({ name: result.name, posts_uuids: [postUuid], user_uuid: user.passport_uuid })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (created) => {
            this.selectionStateService.addSelection(created);
            this.selections.update(list => [...list, created]);
            this.isSaved.set(true);
            this.cdr.markForCheck();
          },
          error: (err) => {
            captureSentryMessage('Selection creation failed', err);
            this.snackbarService.show({ message: 'Something went wrong. Please try again.' });
          },
        });
    });
  }

  private openSelectionsMenu(): void {
    this.selections.set(this.selectionStateService.selections());
    this.selectionsMenuOpen.set(true);
    this.selectionsLoading.set(false);
    this.cdr.markForCheck();
  }

  closeSelectionsMenu(): void {
    this.selectionsMenuOpen.set(false);
    this.flushPendingChanges();
    this.saveToggled.emit({ ...this.item(), is_saved: this.isSavedDisplay() });
    this.cdr.markForCheck();
  }

  private flushPendingChanges(): void {
    if (this.#pendingChanges.size === 0) return;

    const changes = Array.from(this.#pendingChanges.values());
    this.#pendingChanges.clear();

    changes.forEach(({ selectionId, posts_uuids }) => {
      this.selectionService
        .updateSelection(selectionId, { posts_uuids })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (err) => {
            captureSentryMessage('Selection update failed', err);
            this.snackbarService.show({ message: 'Something went wrong. Please try again.' });
          },
        });
    });
  }

  likePost(e: Event): void {
    if (this.isGuest()) {
      e.preventDefault();
      e.stopPropagation();

      this.guestAuthService.handleGuestAuth({
        postId: this.item().post_uuid,
        onCommunityInApp: () => this.openGuestView.emit(),
      });
      return;
    }

    this.vibrateService.vibrate();

    const initialState = {
      isLiked: this.item().is_liked_by_me,
      likesCount: this.item().likes_count,
    };

    const isLikedByMe = !this.item().is_liked_by_me;

    const updatedItem: CommunityItem = {
      ...this.item(),
      is_liked_by_me: isLikedByMe,
      likes_count: this.item().likes_count + (isLikedByMe ? 1 : -1),
    };

    this.itemChange.emit(updatedItem);

    const action = isLikedByMe
      ? this.postService.likeEvent(this.item().post_uuid)
      : this.postService.unlikePost(this.item().post_uuid);

    action.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (isLikedByMe && this.featureFlagService.isShowRecommendationMessageEnabled()) {
          this.snackbarService.show({
            message: `Recommendation is published in your <a href="${this.navigationService.getProfileUrl()}" target="_blank">profile feed</a>.`,
            duration: 3000,
          });
        }
      },
      error: () => {
        const revertedItem: CommunityItem = {
          ...this.item(),
          is_liked_by_me: initialState.isLiked,
          likes_count: initialState.likesCount,
        };
        this.itemChange.emit(revertedItem);
      },
    });
  }

  async sharePost(): Promise<void> {
    const fallbackUrl = this.navigationService.getPostUrl(this.item().post_uuid);
    const link = this.#navConfig?.useExternalPostNavigation
      ? this.externalPostNavService.getPostShareUrl(this.item(), fallbackUrl)
      : fallbackUrl;

    if (this.webShareService.canShare()) {
      const result = await this.webShareService.share({ url: link });
      if (result === 'success') {
        this.shareLinkCopied.emit();
      }
      return;
    }

    this.clipboardService.copyToClipboard(link, 'Link copied to clipboard');
    this.shareLinkCopied.emit();
  }

  onOpenGuestView(): void {
    this.openGuestView.emit();
  }

  onNavigateToPost(event: Event, isMajiWebview: boolean = false): void {
    event.preventDefault();
    event.stopPropagation();

    const targetUrl = isMajiWebview ? TargetUrl.sameWindow : TargetUrl.newWindow;
    this.navigateToPost.emit(targetUrl);
  }
}
